import React from 'react'
import clsx from 'clsx'
import lodash from 'lodash'
import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { Logger } from 'wdc-cube'
import { classToFComponent, FCClassContext, LifesHubColors, MapSlider, MapToolbarGap } from 'src/utils/views'
import { TcmCard } from 'src/utils/views/modules/the-consumer-market'

import Tooltip from '@mui/material/Tooltip'
import AddIcon from '@mui/icons-material/Add'
import CropIcon from '@mui/icons-material/Crop'
import DeleteSweep from '@mui/icons-material/DeleteSweep'
import PanToolOutlinedIcon from '@mui/icons-material/PanToolOutlined'
import RemoveIcon from '@mui/icons-material/Remove'
import RouteIcon from '@mui/icons-material/Route'
import TripOriginIcon from '@mui/icons-material/TripOrigin'

import * as L from 'leaflet'
import { MapContainer, TileLayer } from 'react-leaflet'

import TcmProgress from '../../the-consumer-market/views/helpers/tcm_progress'
import { formatNumber } from '../../the-consumer-market/views/helpers/tcm_view_utils'
import type { MapCardScope, SliderScope } from '../../the-consumer-market/tcm_scopes'
import type { PointsMapScope } from '../mv_scopes'
import type { PontoMapa } from '../mv_types'

const LOG = Logger.get('MV-PointsMap')

const BRASIL_CENTER: L.LatLngExpression = [-14.5, -52]

const MapBox = styled('div')({
    position: 'relative',
    display: 'flex',
    flexGrow: 1,
    minHeight: 0,
    borderRadius: 'var(--radius)',
    border: '1px solid var(--border)',
    overflow: 'hidden',
    '& .leaflet-container': { flexGrow: 1, minHeight: 0, background: '#eef1ef' }
})

const MapCount = styled('div')({
    position: 'absolute',
    right: 10,
    top: 10,
    zIndex: 1000,
    padding: '3px 9px',
    borderRadius: 999,
    backgroundColor: 'rgba(255, 255, 255, .92)',
    border: '1px solid var(--border-strong)',
    fontSize: 11.5,
    fontWeight: 700,
    color: LifesHubColors.muted
})

const POINT_STYLE: L.CircleMarkerOptions = {
    radius: 3.5,
    weight: 1,
    color: LifesHubColors.green800,
    fillColor: LifesHubColors.chartBase,
    fillOpacity: 0.85
}

const SELECTED_STYLE: L.CircleMarkerOptions = {
    radius: 8,
    weight: 2,
    color: LifesHubColors.warning,
    fillColor: '#ffd28a',
    fillOpacity: 1
}

type PointsMapViewProps = {
    className?: string
    scope: PointsMapScope
    ferramentas?: MapCardScope
}

class PointsMapViewClass implements FCClassContext<PointsMapViewProps> {
    scope!: PointsMapScope
    ferramentas?: MapCardScope

    private map: L.Map | null = null
    private layer: L.LayerGroup | null = null
    private markers = new Map<string, L.CircleMarker>()
    private renderedPoints: PontoMapa[] | null = null
    private renderedSelectedId = ''
    private readonly renderer = L.canvas({ padding: 0.5 })
    private readonly resizeObserver = new ResizeObserver(() => this.map?.invalidateSize())
    private boxEl: HTMLDivElement | null = null

    onSyncState({ scope, ferramentas }: PointsMapViewProps) {
        this.scope = scope
        this.ferramentas = ferramentas
        this.syncLayers()
    }

    onDetach() {
        this.resizeObserver.disconnect()
    }

    readonly emitBoxRef = (el: HTMLDivElement | null) => {
        if (this.boxEl) this.resizeObserver.unobserve(this.boxEl)
        this.boxEl = el
        if (el) this.resizeObserver.observe(el)
    }

    readonly emitMapRef = (map: L.Map | null) => {
        if (this.map === map) return
        this.map = map
        this.layer = null
        this.renderedPoints = null
        this.syncLayers()
        this.ferramentas?.onMapChanged(map).catch(LOG.caught)
    }

    readonly getMap = () => this.map

    private syncLayers() {
        const map = this.map
        if (!map || !this.scope) return

        if (this.renderedPoints !== this.scope.points) {
            this.renderedPoints = this.scope.points
            this.renderedSelectedId = ''
            this.rebuild(map, this.scope.points)
        }

        if (this.renderedSelectedId !== this.scope.selectedId) {
            this.markers.get(this.renderedSelectedId)?.setStyle(POINT_STYLE).setRadius(POINT_STYLE.radius!)
            this.renderedSelectedId = this.scope.selectedId
            const selected = this.markers.get(this.renderedSelectedId)
            if (selected) {
                selected.setStyle(SELECTED_STYLE).setRadius(SELECTED_STYLE.radius!).bringToFront()
            }
        }
    }

    private rebuild(map: L.Map, points: PontoMapa[]) {
        this.layer?.remove()
        this.markers.clear()

        const layer = L.layerGroup()
        const bounds = L.latLngBounds([])
        for (const point of points) {
            const marker = L.circleMarker([point.lat, point.lon], {
                ...POINT_STYLE,
                renderer: this.renderer,
                bubblingMouseEvents: false
            })
            marker.bindTooltip(
                () => `<b>${escapeHtml(point.nome)}</b><br/>${escapeHtml(point.municipio)} - ${escapeHtml(point.uf)}`,
                { direction: 'top', offset: [0, -4] }
            )
            if (this.scope.onPointClicked) {
                marker.on('click', () => this.scope.onPointClicked?.(point.id).catch(LOG.caught))
            }
            layer.addLayer(marker)
            this.markers.set(point.id, marker)
            bounds.extend([point.lat, point.lon])
        }
        layer.addTo(map)
        this.layer = layer

        if (points.length === 1) {
            map.setView([points[0].lat, points[0].lon], 14, { animate: false })
        } else if (bounds.isValid()) {
            map.fitBounds(bounds, { maxZoom: 14, animate: false, padding: [12, 12] })
        } else {
            map.setView(BRASIL_CENTER, 4, { animate: false })
        }
    }

    render({ className, scope }: PointsMapViewProps) {
        return (
            <TcmCard className={className} caption={scope.caption}>
                <MapBox ref={this.emitBoxRef}>
                    <MapContainer center={BRASIL_CENTER} zoom={4} preferCanvas scrollWheelZoom ref={this.emitMapRef}>
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    </MapContainer>
                    {this.ferramentas && <MapToolbarView scope={this.ferramentas} getMap={this.getMap} />}
                    {!scope.loaded && <TcmProgress />}
                    {scope.loaded && <MapCount>{formatNumber(scope.points.length)} no mapa</MapCount>}
                </MapBox>
            </TcmCard>
        )
    }
}

function escapeHtml(text: string) {
    return text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`)
}

export const PointsMapView = classToFComponent(PointsMapViewClass, React)

// :: Ferramentas de seleção (mesmo visual da barra do mapa do Mercado IA, tcm_map_card)

const useToolbarStyles = makeStyles()({
    toolButton: {
        width: 34,
        height: 34,
        padding: 5,
        cursor: 'pointer',
        color: '#2d332f',
        backgroundColor: 'var(--surface)',
        borderLeft: '1px solid var(--border-strong)',
        borderRight: '1px solid var(--border-strong)',
        '&:hover': { backgroundColor: '#edf1ee' }
    },
    toolButtonActive: {
        color: 'var(--green-800)',
        backgroundColor: 'var(--green-050)'
    },
    topToolButton: {
        borderTop: '1px solid var(--border-strong)',
        borderTopLeftRadius: 9,
        borderTopRightRadius: 9
    },
    bottomToolButton: {
        borderBottom: '1px solid var(--border-strong)',
        borderBottomLeftRadius: 9,
        borderBottomRightRadius: 9
    }
})

const MapToolbar = styled('div')({
    position: 'absolute',
    top: 10,
    left: 10,
    zIndex: 1100,
    boxShadow: 'var(--shadow-1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    borderRadius: 9
})

type MapToolbarViewProps = {
    scope: MapCardScope
    getMap: () => L.Map | null
}

class MapToolbarViewClass implements FCClassContext<MapToolbarViewProps> {
    scope!: MapCardScope
    getMap!: () => L.Map | null

    onSyncState({ scope, getMap }: MapToolbarViewProps) {
        this.scope = scope
        this.getMap = getMap
    }

    readonly emitZoomIn = () => void this.getMap()?.zoomIn(1)
    readonly emitZoomOut = () => void this.getMap()?.zoomOut(1)
    readonly emitNavegar = () => this.scope.onSetMapMode().catch(LOG.caught)
    readonly emitRota = () => this.scope.onSetRouteMode().catch(LOG.caught)
    readonly emitArea = () => this.scope.onSetAreaMode().catch(LOG.caught)
    readonly emitCirculo = () => this.scope.onSetCircleMode().catch(LOG.caught)
    readonly emitLimpar = () => this.scope.onClearFilters().catch(LOG.caught)

    render({ scope }: MapToolbarViewProps) {
        const { classes } = useToolbarStyles()
        const mode = (active: boolean) => clsx(classes.toolButton, active && classes.toolButtonActive)
        const single = clsx(classes.toolButton, classes.topToolButton, classes.bottomToolButton)
        return (
            <>
                {scope.slider.visible && <MapSliderView scope={scope.slider} />}
                <MapToolbar>
                    <Tooltip title="Aproximar" placement="right">
                        <AddIcon
                            className={clsx(classes.toolButton, classes.topToolButton)}
                            aria-label="Aproximar"
                            onClick={this.emitZoomIn}
                        />
                    </Tooltip>
                    <Tooltip title="Afastar" placement="right">
                        <RemoveIcon
                            className={clsx(classes.toolButton, classes.bottomToolButton)}
                            aria-label="Afastar"
                            onClick={this.emitZoomOut}
                        />
                    </Tooltip>
                    <MapToolbarGap />
                    <Tooltip title="Navegar (sem seleção)" placement="right">
                        <PanToolOutlinedIcon
                            className={clsx(mode(scope.mode === 'map'), classes.topToolButton)}
                            aria-label="Navegar"
                            onClick={this.emitNavegar}
                        />
                    </Tooltip>
                    <Tooltip title="Selecionar por rota" placement="right">
                        <RouteIcon
                            className={mode(scope.mode === 'route')}
                            aria-label="Selecionar por rota"
                            onClick={this.emitRota}
                        />
                    </Tooltip>
                    <Tooltip title="Selecionar por área" placement="right">
                        <CropIcon
                            className={mode(scope.mode === 'polygon')}
                            aria-label="Selecionar por área"
                            onClick={this.emitArea}
                        />
                    </Tooltip>
                    <Tooltip title="Selecionar por círculo (raio)" placement="right">
                        <TripOriginIcon
                            className={clsx(mode(scope.mode === 'circle'), classes.bottomToolButton)}
                            aria-label="Selecionar por círculo"
                            onClick={this.emitCirculo}
                        />
                    </Tooltip>
                    {scope.showClearButton && (
                        <>
                            <MapToolbarGap />
                            <Tooltip title="Limpar seleção do mapa" placement="right">
                                <DeleteSweep
                                    className={single}
                                    aria-label="Limpar seleção do mapa"
                                    onClick={this.emitLimpar}
                                />
                            </Tooltip>
                        </>
                    )}
                </MapToolbar>
            </>
        )
    }
}

const MapToolbarView = classToFComponent(MapToolbarViewClass, React)

type MapSliderViewProps = {
    scope: SliderScope
}

/** Raio do círculo ou largura da rota (mesmo controle do mapa do Mercado IA). */
class MapSliderViewClass implements FCClassContext<MapSliderViewProps> {
    scope!: SliderScope

    onSyncState({ scope }: MapSliderViewProps) {
        this.scope = scope
    }

    readonly emitChange = (_event: Event, value: number | number[]) => {
        if (lodash.isNumber(value)) this.scope.onValueChanged(value).catch(LOG.caught)
    }

    readonly formatLabel = (value: number) => `${value}${this.scope.unit}`

    render({ scope }: MapSliderViewProps) {
        return (
            <MapSlider
                size="small"
                aria-label="Raio de seleção"
                valueLabelDisplay="auto"
                value={scope.value}
                min={scope.min}
                max={scope.max}
                step={scope.step}
                valueLabelFormat={this.formatLabel}
                onChange={this.emitChange}
            />
        )
    }
}

const MapSliderView = classToFComponent(MapSliderViewClass, React)
export default PointsMapView
